import React from "react";
import { ExternalLink, FileSpreadsheet, PlayCircle } from "lucide-react";

/**
 * Compact "data source" card shown above the analysis report.
 * Visually echoes the Google Analytics page (orange GA mark + sparkline)
 * so the reader knows exactly where the numbers came from and can jump back
 * to the source while reading the AI write-up.
 */
function SparklinePreview({ points = [], accent = "#F9AB00" }) {
  // Build a tiny SVG sparkline from the timeseries (sessions if present, else first numeric key).
  const values = (points || [])
    .map((p) => Number(p?.sessions ?? p?.users ?? p?.pageviews ?? 0))
    .filter((n) => !Number.isNaN(n));
  if (values.length < 2) {
    return (
      <svg viewBox="0 0 100 32" className="w-full h-8 opacity-60">
        <line x1="0" y1="28" x2="100" y2="28" stroke="#EAE7DD" strokeWidth="1" />
      </svg>
    );
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = 100 / (values.length - 1);
  const d = values
    .map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(28 - ((v - min) / range) * 24).toFixed(1)}`)
    .join(" ");
  return (
    <svg viewBox="0 0 100 32" className="w-full h-8" preserveAspectRatio="none">
      <line x1="0" y1="28" x2="100" y2="28" stroke="#EAE7DD" strokeWidth="0.5" />
      <path d={d} fill="none" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GAMark() {
  // Simplified Google Analytics orange mark — three columns.
  return (
    <svg viewBox="0 0 32 32" className="w-7 h-7" aria-hidden="true">
      <rect x="4" y="14" width="6" height="14" rx="3" fill="#FBBC04" />
      <rect x="13" y="8" width="6" height="20" rx="3" fill="#E37400" />
      <rect x="22" y="2" width="6" height="26" rx="3" fill="#F9AB00" />
    </svg>
  );
}

export function AnalysisSourceCard({ analysis }) {
  if (!analysis) return null;
  const source = analysis.source;
  const propertyId = analysis.ga_property_id;
  const propertyLabel = analysis.property_label;
  const cur = analysis.current_period || {};
  const ts = analysis.timeseries_current || [];

  if (source === "ga") {
    // Open GA in the user's account on the right property + the same date range.
    const start = cur.start || "";
    const end = cur.end || "";
    const gaUrl = propertyId
      ? `https://analytics.google.com/analytics/web/#/p${propertyId}/reports/intelligenthome?params=_u..nav%3Dmaui%26_u.date00%3D${start.replaceAll("-", "")}%26_u.date01%3D${end.replaceAll("-", "")}`
      : "https://analytics.google.com";
    return (
      <aside
        className="no-print rounded-2xl bg-white border border-black/10 bento-shadow p-4 mb-6 flex items-stretch gap-4"
        data-testid="analysis-source-card"
      >
        <div className="w-12 h-12 rounded-xl bg-[#FFF7E6] grid place-items-center shrink-0">
          <GAMark />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="tag-label text-[#1E392A]/55">Source · Google Analytics</div>
              <div className="font-display text-base truncate">{propertyLabel || "GA4 property"}</div>
              <div className="text-xs text-[#1E392A]/55 mt-0.5">
                {cur.label || `${start} → ${end}`}
              </div>
            </div>
            <a
              href={gaUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-in-ga-link"
              className="text-xs inline-flex items-center gap-1 rounded-full border border-[#1E392A]/15 px-3 py-1.5 hover:bg-[#F7F5F0] text-[#1E392A]/80"
            >
              Open in GA <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="mt-2 max-w-md">
            <SparklinePreview points={ts} accent="#F9AB00" />
          </div>
        </div>
      </aside>
    );
  }

  if (source === "csv") {
    return (
      <aside
        className="no-print rounded-2xl bg-white border border-black/10 bento-shadow p-4 mb-6 flex items-center gap-4"
        data-testid="analysis-source-card"
      >
        <div className="w-12 h-12 rounded-xl bg-[#F7F5F0] grid place-items-center shrink-0">
          <FileSpreadsheet className="w-5 h-5 text-[#1E392A]/70" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="tag-label text-[#1E392A]/55">Source · CSV upload</div>
          <div className="font-display text-base truncate">{propertyLabel || "Uploaded data"}</div>
          <div className="text-xs text-[#1E392A]/55 mt-0.5">{cur.label || ""}</div>
        </div>
      </aside>
    );
  }

  if (source === "demo") {
    return (
      <aside
        className="no-print rounded-2xl bg-white border border-black/10 bento-shadow p-4 mb-6 flex items-center gap-4"
        data-testid="analysis-source-card"
      >
        <div className="w-12 h-12 rounded-xl bg-[#F7F5F0] grid place-items-center shrink-0">
          <PlayCircle className="w-5 h-5 text-[#E27D60]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="tag-label text-[#1E392A]/55">Source · Demo dataset</div>
          <div className="font-display text-base truncate">{propertyLabel || "Plainly demo site"}</div>
          <div className="text-xs text-[#1E392A]/55 mt-0.5">A sample dataset for trying the product</div>
        </div>
      </aside>
    );
  }

  return null;
}
